/**
 * Learned format ceiling (Oct 21 2026): the note when Maestro replays a track at a lower
 * resolution a player can handle, and the Devices line that shows / resets the learned limit.
 * Merged into every catalog by translations.js.
 */
const formatCeilingI18n = {
  en: {
    format_ceiling_replay_cd: "Your {device} player couldn't play hi-res, so Maestro is playing this in CD quality",
    format_ceiling_replay_96k: "Your {device} player couldn't play this resolution, so Maestro is playing this at 24-bit/96 kHz",
    format_ceiling_device_fallback: 'network',
    format_ceiling_learned_cd: "Learned limit: CD quality (16-bit/44.1 kHz). This player couldn't play hi-res, so Maestro sends CD quality.",
    format_ceiling_learned_96k: "Learned limit: 24-bit/96 kHz. This player couldn't play higher resolutions, so Maestro stops at 24/96.",
    format_ceiling_reset: 'Reset learned limit',
    format_ceiling_reset_hint: 'Maestro will try hi-res again on the next play.',
  },
  es: {
    format_ceiling_replay_cd: 'Tu reproductor {device} no pudo reproducir alta resolución, así que Maestro lo está reproduciendo en calidad CD',
    format_ceiling_replay_96k: 'Tu reproductor {device} no pudo reproducir esta resolución, así que Maestro lo está reproduciendo a 24 bits/96 kHz',
    format_ceiling_device_fallback: 'de red',
    format_ceiling_learned_cd: 'Límite aprendido: calidad CD (16 bits/44,1 kHz). Este reproductor no pudo reproducir alta resolución, así que Maestro envía calidad CD.',
    format_ceiling_learned_96k: 'Límite aprendido: 24 bits/96 kHz. Este reproductor no pudo reproducir resoluciones más altas, así que Maestro se detiene en 24/96.',
    format_ceiling_reset: 'Restablecer límite aprendido',
    format_ceiling_reset_hint: 'Maestro volverá a intentar alta resolución en la próxima reproducción.',
  },
  fr: {
    format_ceiling_replay_cd: "Votre lecteur {device} n'a pas pu lire la haute résolution, Maestro le lit donc en qualité CD",
    format_ceiling_replay_96k: "Votre lecteur {device} n'a pas pu lire cette résolution, Maestro le lit donc en 24 bits/96 kHz",
    format_ceiling_device_fallback: 'réseau',
    format_ceiling_learned_cd: "Limite apprise : qualité CD (16 bits/44,1 kHz). Ce lecteur n'a pas pu lire la haute résolution, Maestro envoie donc la qualité CD.",
    format_ceiling_learned_96k: "Limite apprise : 24 bits/96 kHz. Ce lecteur n'a pas pu lire de résolutions plus élevées, Maestro s'arrête donc à 24/96.",
    format_ceiling_reset: 'Réinitialiser la limite apprise',
    format_ceiling_reset_hint: 'Maestro réessaiera la haute résolution à la prochaine lecture.',
  },
  de: {
    format_ceiling_replay_cd: 'Dein Player {device} konnte kein Hi-Res abspielen, deshalb spielt Maestro dies in CD-Qualität',
    format_ceiling_replay_96k: 'Dein Player {device} konnte diese Auflösung nicht abspielen, deshalb spielt Maestro dies mit 24 Bit/96 kHz',
    format_ceiling_device_fallback: 'Netzwerk',
    format_ceiling_learned_cd: 'Erlerntes Limit: CD-Qualität (16 Bit/44,1 kHz). Dieser Player konnte kein Hi-Res abspielen, deshalb sendet Maestro CD-Qualität.',
    format_ceiling_learned_96k: 'Erlerntes Limit: 24 Bit/96 kHz. Dieser Player konnte keine höheren Auflösungen abspielen, deshalb bleibt Maestro bei 24/96.',
    format_ceiling_reset: 'Erlerntes Limit zurücksetzen',
    format_ceiling_reset_hint: 'Maestro versucht beim nächsten Abspielen wieder Hi-Res.',
  },
  it: {
    format_ceiling_replay_cd: "Il tuo lettore {device} non è riuscito a riprodurre l'alta risoluzione, quindi Maestro lo sta riproducendo in qualità CD",
    format_ceiling_replay_96k: 'Il tuo lettore {device} non è riuscito a riprodurre questa risoluzione, quindi Maestro lo sta riproducendo a 24 bit/96 kHz',
    format_ceiling_device_fallback: 'di rete',
    format_ceiling_learned_cd: "Limite appreso: qualità CD (16 bit/44,1 kHz). Questo lettore non è riuscito a riprodurre l'alta risoluzione, quindi Maestro invia qualità CD.",
    format_ceiling_learned_96k: 'Limite appreso: 24 bit/96 kHz. Questo lettore non è riuscito a riprodurre risoluzioni più alte, quindi Maestro si ferma a 24/96.',
    format_ceiling_reset: 'Reimposta limite appreso',
    format_ceiling_reset_hint: "Maestro riproverà l'alta risoluzione alla prossima riproduzione.",
  },
  pt: {
    format_ceiling_replay_cd: 'Seu player {device} não conseguiu reproduzir alta resolução, então o Maestro está tocando isto em qualidade de CD',
    format_ceiling_replay_96k: 'Seu player {device} não conseguiu reproduzir esta resolução, então o Maestro está tocando isto em 24 bits/96 kHz',
    format_ceiling_device_fallback: 'de rede',
    format_ceiling_learned_cd: 'Limite aprendido: qualidade de CD (16 bits/44,1 kHz). Este player não conseguiu reproduzir alta resolução, então o Maestro envia qualidade de CD.',
    format_ceiling_learned_96k: 'Limite aprendido: 24 bits/96 kHz. Este player não conseguiu reproduzir resoluções mais altas, então o Maestro para em 24/96.',
    format_ceiling_reset: 'Redefinir limite aprendido',
    format_ceiling_reset_hint: 'O Maestro tentará alta resolução de novo na próxima reprodução.',
  },
  ja: {
    format_ceiling_replay_cd: '{device} プレーヤーはハイレゾを再生できなかったため、Maestro は CD 品質で再生しています',
    format_ceiling_replay_96k: '{device} プレーヤーはこの解像度を再生できなかったため、Maestro は 24 ビット/96 kHz で再生しています',
    format_ceiling_device_fallback: 'ネットワーク',
    format_ceiling_learned_cd: '学習した上限: CD 品質（16 ビット/44.1 kHz）。このプレーヤーはハイレゾを再生できなかったため、Maestro は CD 品質で送信します。',
    format_ceiling_learned_96k: '学習した上限: 24 ビット/96 kHz。このプレーヤーはそれ以上の解像度を再生できなかったため、Maestro は 24/96 までにします。',
    format_ceiling_reset: '学習した上限をリセット',
    format_ceiling_reset_hint: '次の再生で Maestro はもう一度ハイレゾを試します。',
  },
  ko: {
    format_ceiling_replay_cd: '{device} 플레이어가 하이레스를 재생하지 못해 Maestro가 CD 품질로 재생하고 있어요',
    format_ceiling_replay_96k: '{device} 플레이어가 이 해상도를 재생하지 못해 Maestro가 24비트/96kHz로 재생하고 있어요',
    format_ceiling_device_fallback: '네트워크',
    format_ceiling_learned_cd: '학습된 한도: CD 품질(16비트/44.1kHz). 이 플레이어가 하이레스를 재생하지 못해 Maestro가 CD 품질로 보냅니다.',
    format_ceiling_learned_96k: '학습된 한도: 24비트/96kHz. 이 플레이어가 더 높은 해상도를 재생하지 못해 Maestro가 24/96까지만 보냅니다.',
    format_ceiling_reset: '학습된 한도 초기화',
    format_ceiling_reset_hint: '다음 재생 때 Maestro가 하이레스를 다시 시도합니다.',
  },
  zh: {
    format_ceiling_replay_cd: '您的 {device} 播放器无法播放高解析度音频，因此 Maestro 正以 CD 音质播放',
    format_ceiling_replay_96k: '您的 {device} 播放器无法播放此解析度，因此 Maestro 正以 24 位/96 kHz 播放',
    format_ceiling_device_fallback: '网络',
    format_ceiling_learned_cd: '已学习的上限：CD 音质（16 位/44.1 kHz）。此播放器无法播放高解析度音频，因此 Maestro 发送 CD 音质。',
    format_ceiling_learned_96k: '已学习的上限：24 位/96 kHz。此播放器无法播放更高解析度，因此 Maestro 最高发送 24/96。',
    format_ceiling_reset: '重置已学习的上限',
    format_ceiling_reset_hint: '下次播放时 Maestro 会再次尝试高解析度。',
  },
  nl: {
    format_ceiling_replay_cd: 'Je {device}-speler kon geen hi-res afspelen, dus Maestro speelt dit af in cd-kwaliteit',
    format_ceiling_replay_96k: 'Je {device}-speler kon deze resolutie niet afspelen, dus Maestro speelt dit af op 24-bit/96 kHz',
    format_ceiling_device_fallback: 'netwerk',
    format_ceiling_learned_cd: 'Geleerde limiet: cd-kwaliteit (16-bit/44,1 kHz). Deze speler kon geen hi-res afspelen, dus Maestro stuurt cd-kwaliteit.',
    format_ceiling_learned_96k: 'Geleerde limiet: 24-bit/96 kHz. Deze speler kon geen hogere resoluties afspelen, dus Maestro stopt bij 24/96.',
    format_ceiling_reset: 'Geleerde limiet resetten',
    format_ceiling_reset_hint: 'Maestro probeert bij de volgende keer afspelen weer hi-res.',
  },
  sv: {
    format_ceiling_replay_cd: 'Din {device}-spelare kunde inte spela hi-res, så Maestro spelar det här i CD-kvalitet',
    format_ceiling_replay_96k: 'Din {device}-spelare kunde inte spela den här upplösningen, så Maestro spelar det här i 24 bitar/96 kHz',
    format_ceiling_device_fallback: 'nätverks',
    format_ceiling_learned_cd: 'Inlärd gräns: CD-kvalitet (16 bitar/44,1 kHz). Den här spelaren kunde inte spela hi-res, så Maestro skickar CD-kvalitet.',
    format_ceiling_learned_96k: 'Inlärd gräns: 24 bitar/96 kHz. Den här spelaren kunde inte spela högre upplösningar, så Maestro stannar vid 24/96.',
    format_ceiling_reset: 'Återställ inlärd gräns',
    format_ceiling_reset_hint: 'Maestro provar hi-res igen nästa gång du spelar.',
  },
};

export default formatCeilingI18n;
